// Tenant-admin management of Database Gateways: list paired gateways with
// live online/offline status, add one (shows the one-time pairing token +
// download), rotate/revoke, and view a gateway's advertised databases and
// (opt-in) aggregate access summary.
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  Loader2, Server, ArrowLeft, Plus, RefreshCw, Download, Trash2,
  KeyRound, Copy, X, CheckCircle2, Circle,
} from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { useVisiblePolling } from '@/hooks/useVisiblePolling'
import {
  listGateways, createGateway, rotateGateway, revokeGateway,
  downloadGatewayPackage, gatewayAccessSummary,
  type Gateway, type GatewayAccessSummary,
} from '@/api/gateways'

function relTime(iso?: string | null): string {
  if (!iso) return 'never'
  const then = new Date(iso).getTime()
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (secs < 60) return `${secs}s ago`
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`
  return `${Math.floor(secs / 86400)}d ago`
}

function StatusDot({ online }: { online: boolean }) {
  return online
    ? <CheckCircle2 size={15} style={{ color: 'var(--color-success)' }} />
    : <Circle size={15} style={{ color: 'var(--color-text-faint)' }} />
}

/** Token reveal shown once after create/rotate — copyable, never refetchable. */
function PairingTokenPanel({ token, onClose }: { token: string; onClose: () => void }) {
  const setup = `TWASTA_PAIRING_TOKEN=${token} ./setup.sh`
  return (
    <div style={{
      border: '1px solid var(--color-border-strong)', borderRadius: 8,
      background: 'var(--color-bg-secondary)', padding: 16, margin: '12px 0',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong style={{ fontSize: 15 }}>Pairing token — shown once</strong>
        <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={15} /></button>
      </div>
      <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', margin: '6px 0 10px' }}>
        Copy it now — it is not retrievable again and expires in 30 minutes.
        Paste it into the gateway app&apos;s &ldquo;Connect to twasta&rdquo; screen
        (Pairing token tab), or use the one-liner below. Tokens are optional:
        the gateway&apos;s setup can also register itself with your twasta login.
      </p>
      {[token, setup].map((val, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input
            readOnly
            value={val}
            style={{
              flex: 1, fontFamily: "'IBM Plex Mono', monospace", fontSize: 13.5,
              padding: '8px 10px', border: '1px solid var(--color-border-strong)',
              borderRadius: 6, background: 'var(--color-input-bg)', color: 'var(--color-text)',
            }}
          />
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => { void navigator.clipboard.writeText(val); toast.success('Copied') }}
          >
            <Copy size={14} /> Copy
          </button>
        </div>
      ))}
    </div>
  )
}

export default function GatewaysPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const isAdmin = user?.role === 'ENTERPRISE_ADMIN'
  const [gateways, setGateways] = useState<Gateway[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [revealToken, setRevealToken] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)
  const [summaryFor, setSummaryFor] = useState<string | null>(null)
  const [summary, setSummary] = useState<GatewayAccessSummary | null>(null)

  // `background` = a poll refresh: it must not flash the list back to a
  // spinner, and a transient failure must not raise a toast every cycle.
  const load = useCallback(async (background = false) => {
    if (!background) setLoading(true)
    try {
      setGateways(await listGateways())
    } catch {
      if (!background) toast.error('Could not load gateways')
    } finally {
      if (!background) setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])
  // Keep online/offline reasonably live, but only while the tab is actually
  // being looked at — a forgotten tab polling forever is just access-log
  // noise. Coming back to the tab refreshes immediately.
  const refresh = useCallback(() => load(true), [load])
  useVisiblePolling(refresh, 30000)

  const handleCreate = async () => {
    if (!newName.trim()) { toast.error('Give the gateway a name'); return }
    setCreating(true)
    try {
      const { pairing_token } = await createGateway(newName.trim())
      setRevealToken(pairing_token)
      setNewName('')
      setShowCreate(false)
      await load()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Could not create gateway')
    } finally {
      setCreating(false)
    }
  }

  const handleRotate = async (g: Gateway) => {
    try {
      setRevealToken(await rotateGateway(g.id))
      toast.success('New pairing token issued')
      await load()
    } catch {
      toast.error('Could not rotate')
    }
  }

  const handleRevoke = async (g: Gateway) => {
    if (!window.confirm(`Revoke "${g.name}"? Its live connection is closed and it must be re-paired to reconnect.`)) return
    try {
      await revokeGateway(g.id)
      toast.success('Gateway revoked')
      await load()
    } catch {
      toast.error('Could not revoke')
    }
  }

  const handleDownload = async () => {
    setDownloading(true)
    try {
      await downloadGatewayPackage()
      toast.success('Gateway package downloaded')
    } catch {
      toast.error('Download failed')
    } finally {
      setDownloading(false)
    }
  }

  const toggleSummary = async (g: Gateway) => {
    if (summaryFor === g.id) { setSummaryFor(null); setSummary(null); return }
    setSummaryFor(g.id)
    setSummary(null)
    try {
      setSummary(await gatewayAccessSummary(g.id))
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      toast.error(status === 503 ? 'Gateway is offline'
        : status === 403 ? 'This gateway does not share access summaries'
        : 'Could not load summary')
      setSummaryFor(null)
    }
  }

  if (!isAdmin) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-secondary)' }}>
        <Server size={40} style={{ opacity: 0.4 }} />
        <p>Database Gateways are managed by Enterprise Admins.</p>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>Go back</button>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}><ArrowLeft size={16} /></button>
        <Server size={22} />
        <h1 style={{ fontSize: 23, margin: 0 }}>Database Gateways</h1>
      </div>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 15, margin: '0 0 18px 42px' }}>
        Query databases inside a customer firewall. A gateway runs on-prem,
        connects outbound to twasta, and proxies read-only analytics queries —
        credentials never leave the customer network. Bind a gateway database
        in a project&apos;s Analytics → Connections (type &ldquo;Twasta Gateway&rdquo;).
      </p>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
          <Plus size={16} /> Add gateway
        </button>
        <button className="btn btn-secondary btn-sm" onClick={handleDownload} disabled={downloading}>
          {downloading ? <Loader2 size={16} className="spin" /> : <Download size={16} />} Download gateway app
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => void load()}>
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      {showCreate && (
        <div style={{
          border: '1px solid var(--color-border)', borderRadius: 8,
          padding: 14, marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center',
        }}>
          <input
            autoFocus
            placeholder="Gateway name (e.g. Factory A)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void handleCreate()}
            style={{
              flex: 1, padding: '8px 11px', border: '1px solid var(--color-border)',
              borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)',
            }}
          />
          <button className="btn btn-primary btn-sm" onClick={handleCreate} disabled={creating}>
            {creating ? <Loader2 size={15} className="spin" /> : 'Create'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowCreate(false)}>Cancel</button>
        </div>
      )}

      {revealToken && <PairingTokenPanel token={revealToken} onClose={() => setRevealToken(null)} />}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Loader2 size={26} className="spin" /></div>
      ) : gateways.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: 48, color: 'var(--color-text-secondary)',
          border: '1px dashed var(--color-border)', borderRadius: 10,
        }}>
          <Server size={34} style={{ opacity: 0.4 }} />
          <p>
            No gateways yet. Download the app and run <code>./setup.sh</code> inside
            your network — it asks for this twasta URL and your twasta login and
            registers the gateway here for you. (Prefer to pre-create it? Use
            &ldquo;Add gateway&rdquo; and pair with the one-time token.)
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {gateways.map((g) => (
            <div key={g.id} style={{
              border: '1px solid var(--color-border)', borderRadius: 10, padding: 16,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <StatusDot online={g.online} />
                <strong style={{ fontSize: 16.5 }}>{g.name}</strong>
                <span style={{
                  fontSize: 13, padding: '1px 8px', borderRadius: 6,
                  border: '1px solid var(--color-border)',
                  color: g.status === 'active' ? 'var(--color-success)'
                    : g.status === 'revoked' ? 'var(--color-error)' : 'var(--color-text-faint)',
                }}>{g.status}</span>
                {g.gateway_version && (
                  <span style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>v{g.gateway_version}</span>
                )}
                <span style={{ fontSize: 13.5, color: 'var(--color-text-secondary)', marginLeft: 'auto' }}>
                  {g.online ? `online · ${g.in_flight ?? 0} in flight` : `last seen ${relTime(g.last_seen_at)}`}
                </span>
              </div>

              {g.pairing_pending && (
                <p style={{ fontSize: 13.5, color: 'var(--color-warning, #b78100)', margin: '8px 0 0' }}>
                  Waiting for this gateway to pair (run the app with the pairing token).
                </p>
              )}

              <div style={{ marginTop: 10 }}>
                <span style={{ fontSize: 13.5, color: 'var(--color-text-secondary)' }}>Databases: </span>
                {g.databases.length === 0
                  ? <span style={{ fontSize: 13.5, color: 'var(--color-text-faint)' }}>none announced yet</span>
                  : g.databases.map((d) => (
                    <span key={d.alias} style={{
                      display: 'inline-block', fontSize: 13, fontFamily: "'IBM Plex Mono', monospace",
                      padding: '2px 8px', margin: '0 6px 6px 0', borderRadius: 6,
                      background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
                    }}>{d.alias}{d.dbtype ? ` · ${d.dbtype}` : ''}</span>
                  ))}
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                <button className="btn btn-ghost btn-sm" onClick={() => void handleRotate(g)} disabled={g.status === 'revoked'}>
                  <KeyRound size={14} /> Rotate token
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => void toggleSummary(g)} disabled={!g.online}>
                  {summaryFor === g.id ? 'Hide' : 'Access summary'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => void handleRevoke(g)}
                  disabled={g.status === 'revoked'}
                  style={{ color: 'var(--color-error)', marginLeft: 'auto' }}>
                  <Trash2 size={14} /> Revoke
                </button>
              </div>

              {summaryFor === g.id && (
                <div style={{ marginTop: 12, borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
                  {!summary ? <Loader2 size={18} className="spin" /> : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, fontSize: 14 }}>
                      <div>
                        <strong style={{ fontSize: 13.5, color: 'var(--color-text-secondary)' }}>Top users (queries)</strong>
                        {(summary.by_user || []).slice(0, 6).map((u) => (
                          <div key={u.user_email} style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                            <span>{u.user_email}</span><span>{u.queries}</span>
                          </div>
                        ))}
                      </div>
                      <div>
                        <strong style={{ fontSize: 13.5, color: 'var(--color-text-secondary)' }}>By database (queries)</strong>
                        {(summary.by_alias || []).slice(0, 6).map((a) => (
                          <div key={a.alias} style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                            <span>{a.alias}</span><span>{a.queries}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
